import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import {
  Button,
  ControllerModal,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@bymason-private/legacy-pattern-components';
import { ApiKeysModel, useDeleteApiKey } from '@bymason/controller-api-hooks';

interface DeleteApiKeyModalProps {
  open: boolean;
  apiKey: ApiKeysModel;
  onClose: () => void;
  onSuccess?: (keyName: string, idPrefix: string) => void;
  onError?: (message: string, code?: number) => void;
}

export function DeleteApiKeyModal({ open, apiKey, onClose, onSuccess, onError }: DeleteApiKeyModalProps) {
  const { authToken = '' } = useAuth();
  const [deleteApiKey, deleteError, deleteBusy] = useDeleteApiKey(authToken);
  const [pendingDelete, setPendingDelete] = useState(false);

  const handleDeleteApiKey = useCallback(() => {
    if (apiKey) {
      setPendingDelete(true);
      deleteApiKey(apiKey.self.replace('apikey/', ''));
    }
  }, [apiKey, deleteApiKey]);

  useEffect(() => {
    if (pendingDelete && !deleteBusy && !deleteError) {
      setPendingDelete(false);
      onSuccess && onSuccess(apiKey.desc, apiKey.self.replace('apikey/', ''));
      onClose();
    }
  }, [pendingDelete, setPendingDelete, deleteBusy, deleteError, apiKey, onClose, onSuccess]);

  useEffect(() => {
    if (deleteError && !deleteBusy) {
      onError && onError(deleteError);
      setPendingDelete(false);
    }
  }, [pendingDelete, setPendingDelete, deleteBusy, deleteError, onError]);

  return (
    <ControllerModal onClose={onClose} open={open} title='Delete API key'>
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          <Typography>{`Are you sure you want to delete the API key "${apiKey.desc}"? You cannot undo this action.`}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={onClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button color='error' size='medium' variant='contained' onClick={handleDeleteApiKey}>
          Delete API key
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}
